import {
  ACTIONS,
  actionSkills,
  findAction,
  type ActionDefinition,
  type ActionRequirement,
} from "@afar-research/agent-content/actions";
import type { DesignActionId, ThreadActionSelection } from "@afar-research/contracts";

import type { BrainAction } from "./brainAction.ts";

/**
 * The Action a composer holds, resolved against the catalog: a built-in Action,
 * or an Action that a project or user Skill Bundle declared for itself.
 */
export type ResolvedThreadAction =
  | {
      readonly type: "built-in";
      readonly selection: ThreadActionSelection;
      readonly definition: ActionDefinition;
    }
  | {
      readonly type: "skill";
      readonly selection: ThreadActionSelection;
      readonly skillName: string;
      readonly label: string;
    };

/** The selection prefix of an Action a Skill Bundle declared outside the App's catalog. */
export const SKILL_ACTION_ID_PREFIX = "skill:";

/** A selection for the Action a project or user Skill Bundle declared. */
export function skillActionSelection(skillName: string): ThreadActionSelection {
  return { id: `${SKILL_ACTION_ID_PREFIX}${skillName}` };
}

/**
 * Resolve what a message was sent with. A built-in Action whose variant no
 * longer exists falls back to the Action itself rather than to nothing, so an
 * old message still says what it was.
 */
export function resolveThreadAction(
  selection: ThreadActionSelection | null | undefined,
): ResolvedThreadAction | null {
  if (selection === null || selection === undefined) return null;
  if (selection.id.startsWith(SKILL_ACTION_ID_PREFIX)) {
    const skillName = selection.id.slice(SKILL_ACTION_ID_PREFIX.length);
    return skillName.length === 0
      ? null
      : { type: "skill", selection, skillName, label: skillName };
  }
  const definition = findAction(selection.id);
  return definition === undefined ? null : { type: "built-in", selection, definition };
}

/** The Skill Bundles a picked Action invokes, in order. */
export function threadActionSkillNames(action: ResolvedThreadAction): ReadonlyArray<string> {
  return action.type === "skill"
    ? [action.skillName]
    : actionSkills(action.definition, action.selection.variant);
}

/** What a chip says: the Action, then its variant when it has one. */
export function threadActionLabel(action: ResolvedThreadAction): string {
  if (action.type === "skill") return action.label;
  const variant = action.definition.variants?.find(
    (candidate) => candidate.id === action.selection.variant,
  );
  return variant === undefined
    ? action.definition.label
    : `${action.definition.label} · ${variant.label}`;
}

/** The Actions of one kind, in catalog order. */
export function listThreadActions(kind: ActionDefinition["kind"]): ReadonlyArray<ActionDefinition> {
  return ACTIONS.filter((action) => action.kind === kind);
}

/** What a thread offers that an Action may require. */
export interface ThreadActionContext {
  /** The thread runs on the Primary Checkout rather than a Thread Worktree. */
  readonly onPrimaryCheckout: boolean;
  /** The thread's Agent Provider can run native subagents. */
  readonly nativeSubagents: boolean;
}

export type ThreadActionAvailability =
  | { readonly available: true }
  | { readonly available: false; readonly reason: string };

const REQUIREMENT_REASONS: Readonly<Record<ActionRequirement, string>> = {
  "primary-checkout":
    "Design Systems live on the Primary Checkout. Start a thread there to change them.",
  "native-subagents":
    "This provider can't run independent subagents. Pick a provider that can to ask a council.",
};

const REQUIREMENT_MET: Readonly<
  Record<ActionRequirement, (context: ThreadActionContext) => boolean>
> = {
  "primary-checkout": (context) => context.onPrimaryCheckout,
  "native-subagents": (context) => context.nativeSubagents,
};

/**
 * Whether an Action can run in a thread, and why not when it can't. Clients
 * show an unavailable Action disabled with this reason rather than hiding it;
 * the Environment enforces the Design rule again when the turn starts.
 */
export function threadActionAvailability(
  action: Pick<ActionDefinition, "requires">,
  context: ThreadActionContext,
): ThreadActionAvailability {
  for (const requirement of action.requires ?? []) {
    if (!REQUIREMENT_MET[requirement](context)) {
      return { available: false, reason: REQUIREMENT_REASONS[requirement] };
    }
  }
  return { available: true };
}

const BRAIN_ACTION_BY_ID: Readonly<Record<string, BrainAction>> = {
  ingest: "ingest",
  "brain-lint": "lint",
  "brain-delete": "delete",
};

/** The Brain work a selection does, or null when it is not a Brain Action. */
export function brainActionOf(
  selection: ThreadActionSelection | null | undefined,
): BrainAction | null {
  return selection == null ? null : (BRAIN_ACTION_BY_ID[selection.id] ?? null);
}

const DESIGN_VARIANT_OPERATIONS: Readonly<Record<string, DesignActionId>> = {
  create: "create-design",
  update: "update-design",
  template: "add-design-template",
  example: "add-design-example",
  sample: "create-design-sample",
};

/**
 * The Design Operation a selection submits, or null when it submits none. The
 * Design Action names its operation by variant; Rename and Delete are
 * housekeeping Actions of their own. A Design Action whose variant is not
 * picked yet submits nothing: it is a chip waiting for its operation.
 */
export function designOperationOf(
  selection: ThreadActionSelection | null | undefined,
): DesignActionId | null {
  if (selection == null) return null;
  if (selection.id === "design-rename") return "rename-design-system";
  if (selection.id === "design-delete") return "delete-design-system";
  if (selection.id !== "design" || selection.variant === undefined) return null;
  return DESIGN_VARIANT_OPERATIONS[selection.variant] ?? null;
}

/** The selection that submits a Design Operation, the inverse of `designOperationOf`. */
export function designSelectionFor(operation: DesignActionId): ThreadActionSelection {
  if (operation === "rename-design-system") return { id: "design-rename" };
  if (operation === "delete-design-system") return { id: "design-delete" };
  const variant = Object.entries(DESIGN_VARIANT_OPERATIONS).find(
    ([, candidate]) => candidate === operation,
  )?.[0];
  return variant === undefined ? { id: "design" } : { id: "design", variant };
}

/** What the project already holds, read from its files. */
export interface ProjectActionFacts {
  readonly hasBrainSources: boolean;
  readonly hasExperimentEvidence: boolean;
  readonly hasManuscript: boolean;
}

/**
 * The one Action the composer suggests next (CONTEXT.md: Next Action).
 * Project files are authoritative: a message's Action records an attempt,
 * not proof that its output exists. It is a suggestion, never a lock.
 */
export function nextThreadAction(input: { readonly project: ProjectActionFacts }): string | null {
  if (!input.project.hasBrainSources) return "ingest";
  if (!input.project.hasExperimentEvidence) return "build";
  if (!input.project.hasManuscript) return "write";
  return "review";
}

const EXPERIMENT_FOLDER_PREFIX = ".afar-research/experiment/";
const BRAIN_RAW_PREFIX = ".afar-research/brain/raw/";
const MANUSCRIPT_PREFIX = "manuscript/";

/**
 * Read the Next Action's project facts off a generic project listing, so no
 * Feature Workspace needs an index of its own for them.
 */
export function projectActionFactsFromEntries(
  entries: ReadonlyArray<{ readonly path: string; readonly kind: "file" | "directory" }>,
): ProjectActionFacts {
  let hasBrainSources = false;
  let hasExperimentEvidence = false;
  let hasManuscript = false;
  for (const entry of entries) {
    if (entry.kind !== "file") continue;
    if (entry.path.startsWith(BRAIN_RAW_PREFIX)) hasBrainSources = true;
    else if (
      entry.path.startsWith(EXPERIMENT_FOLDER_PREFIX) &&
      entry.path.slice(EXPERIMENT_FOLDER_PREFIX.length).includes("/")
    ) {
      hasExperimentEvidence = true;
    } else if (entry.path.startsWith(MANUSCRIPT_PREFIX)) hasManuscript = true;
  }
  return { hasBrainSources, hasExperimentEvidence, hasManuscript };
}

/** Whether a turn's changed files touched the Experiment Workspace, which earns it an Experiment Card. */
export function changedFilesTouchExperiments(
  files: ReadonlyArray<{ readonly path: string }>,
): boolean {
  return files.some((file) => file.path.startsWith(EXPERIMENT_FOLDER_PREFIX));
}
